import type { Area } from "react-easy-crop";

const JPEG_QUALITY = 0.92;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", () =>
      reject(new Error("Failed to load image for crop")),
    );
    image.crossOrigin = "anonymous";
    image.src = src;
  });
}

/** Crop an image to the given pixel area and return a JPEG File. */
export async function getCroppedImageFile(
  imageSrc: string,
  pixelCrop: Area,
  originalFilename: string,
): Promise<File> {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement("canvas");
  const width = Math.max(1, Math.round(pixelCrop.width));
  const height = Math.max(1, Math.round(pixelCrop.height));
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Canvas 2D context unavailable");
  }

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    width,
    height,
  );

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (result) => {
        if (result) {
          resolve(result);
          return;
        }
        reject(new Error("Failed to encode cropped image"));
      },
      "image/jpeg",
      JPEG_QUALITY,
    );
  });

  const lastDot = originalFilename.lastIndexOf(".");
  const stem =
    lastDot > 0 ? originalFilename.slice(0, lastDot) : originalFilename || "image";
  return new File([blob], `${stem}.jpg`, {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
}
